import { Fragment, useEffect, useMemo, useRef } from 'react';
import { Badge, Button, Spinner, Table } from 'react-bootstrap';
import { ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import type { DeploymentRecord } from '@/services/deploymentService';
import { DoNotDeployTag } from '@/components/clientFlags/ClientTags';
import { useClientFlags } from '@/hooks/useClientFlags';
import { useDeployClock } from '@/hooks/useDeployClock';
import { PROGRESS_PRIORITY, useDeployProgress } from '@/hooks/useDeployProgress';
import type { FailureInfo } from '@/utils/deployFailureStore';
import type { PlanInfo } from '@/utils/deployPlanStore';
import { describePlanOutcome } from '@/utils/deployProgress';
import {
  groupCounts,
  groupDisplayName,
  imageDisplay,
  isFailed,
  isFinishedPlan,
  isGroupMember,
  isGroupSummary,
  isLive,
  isStuck,
  shortUser,
  statusKey,
  type ImageDisplay,
  type ImagesByChannel,
} from '@/utils/deployStatus';
import { formatDuration, formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import { FailureLine } from './FailureLine';
import { GroupRowProgress, RowProgress } from './RowProgress';
import { RunDetails } from './RunDetails';
import { RunMoreMenu, type RunMenuHandlers } from './RunMoreMenu';
import { StatusPill } from './StatusPill';
import './deployments.css';

interface HistoryTableProps extends RunMenuHandlers {
  rows: DeploymentRecord[];
  images?: ImagesByChannel;
  failures: Record<string, FailureInfo | undefined>;
  /** What each finished plan-only run found, read from its log. */
  plans?: Record<string, PlanInfo | undefined>;
  /** The newest run for each client, to tell an old failure from a current one. */
  newestByClient?: Map<string, DeploymentRecord>;
  /** Clients with a deploy running now. "Run again" is off for them. */
  liveClients?: Set<string>;
  /** A run to bring into view and mark for a moment: the one just started on this page. */
  highlightId?: string | null;
  /** The member rows of live group runs, for their running counts. */
  groupMembers?: Record<string, DeploymentRecord[]>;
  expanded: Record<string, boolean>;
  onToggle: (deploymentId: string) => void;
  loading: boolean;
  emptyText: string;
  /** "Client" for the history, "Group" for the list of group runs. */
  subjectHeading?: string;
}

const COLUMNS = 8;
const BUSY_REASON = 'A deploy for this client is running now.';

/** How long a run took. A live run counts up each second; a finished one never re-renders. */
function RunDuration({ record }: { record: DeploymentRecord }) {
  const live = isLive(record);
  const now = useDeployClock(live);
  return (
    <>
      <span className="numeric">{formatDuration(record.startedAt, record.endedAt, now)}</span>
      {live && isStuck(record, now) && (
        <div
          className="deploy-sub deploy-sub--danger"
          title="Running for over two hours. A deploy stops itself at two hours, so this run is probably dead. Use More, then Mark failed."
        >
          Stuck?
        </div>
      )}
    </>
  );
}

/** A short line under the status for a run a person ended or signed off. */
function statusNote(record: DeploymentRecord): { text: string; title?: string } | null {
  const status = statusKey(record);
  if (status === 'resolved' && record.resolvedBy) {
    return {
      text: `Resolved by ${shortUser(record.resolvedBy)}`,
      title: [record.resolvedBy, formatNzDateTime(record.resolvedAt), record.resolvedNote].filter(Boolean).join('\n'),
    };
  }
  if (status === 'stopped') {
    const by = /^stopped by (.+)$/i.exec(record.message || '');
    if (by) return { text: `by ${shortUser(by[1])}`, title: by[1] };
  }
  return null;
}

/** When a run started: how long ago, with the exact New Zealand time under it. */
function Started({ at }: { at?: string }) {
  const exact = formatNzDateTime(at);
  return (
    <>
      <div className="deploy-cut" title={exact}>
        {timeAgo(at)}
      </div>
      <div className="deploy-sub deploy-cut" title={exact}>
        {exact}
      </div>
    </>
  );
}

/**
 * The image cell: the name on one line, cut with an ellipsis when it is long
 * (the whole name and the tag show on hover). `under` is a second line, used
 * for the label once the Label column is folded away on a narrow window.
 */
function ImageCell({ image, plan, under }: { image: ImageDisplay; plan?: boolean; under?: string }) {
  return (
    <>
      <div className="deploy-name">
        <span
          className={image.named ? 'deploy-cut' : 'deploy-cut mono'}
          title={image.named ? `${image.label} (${image.tag})` : image.tag}
        >
          {image.label}
        </span>
        {image.dev && <Badge bg="warning">Dev</Badge>}
        {plan && <Badge bg="secondary">Plan only</Badge>}
      </div>
      {under && (
        <div className="deploy-sub deploy-cut deploy-name__under" title={under}>
          {under}
        </div>
      )}
    </>
  );
}

/**
 * The list of runs. A failed run says why in a line of its own, and every
 * action beyond the log sits in one menu. The columns have set widths, so the
 * table keeps its shape whatever the rows hold: long text is cut to one line
 * and shown whole on hover.
 */
export function HistoryTable({
  rows,
  images,
  failures,
  plans,
  highlightId,
  newestByClient,
  liveClients,
  groupMembers,
  expanded,
  onToggle,
  loading,
  emptyText,
  subjectHeading = 'Client',
  ...handlers
}: HistoryTableProps) {
  // Which clients are marked do not deploy. One read for the whole table.
  const flags = useClientFlags();
  // Every running row carries its own progress line. The shared poller reads each run once, however
  // many places show it, so a run that also has a card at the top of the page costs nothing more here.
  const liveRuns = useMemo(
    () => rows.filter((row) => isLive(row) && !isGroupSummary(row) && !isGroupMember(row)),
    [rows]
  );
  const liveMembers = useMemo(() => rows.filter((row) => isLive(row) && isGroupMember(row)), [rows]);
  const runProgress = useDeployProgress(liveRuns, PROGRESS_PRIORITY.run);
  const memberProgress = useDeployProgress(liveMembers, PROGRESS_PRIORITY.member);

  // The run just started on this page: scroll its row into view once it is in the list.
  const table = useRef<HTMLTableElement>(null);
  const highlightShown = Boolean(highlightId) && rows.some((row) => row.deploymentId === highlightId);
  useEffect(() => {
    if (!highlightShown) return;
    table.current?.querySelector('tr.deploy-row--new')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightShown, highlightId]);
  const detailsToggle = (record: DeploymentRecord, open: boolean) => (
    <Button
      size="sm"
      variant="outline-secondary"
      className="deploy-actions__toggle"
      onClick={() => onToggle(record.deploymentId)}
      aria-expanded={open}
      aria-label={open ? 'Hide details' : 'Show details'}
      title={open ? 'Hide details' : 'Show details'}
    >
      {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
    </Button>
  );

  const renderGroup = (record: DeploymentRecord) => {
    const open = Boolean(expanded[record.deploymentId]);
    const counts = groupCounts(record, groupMembers?.[record.deploymentId]);
    const name = groupDisplayName(record);
    const done = counts.total > 0 ? `${counts.done} of ${counts.total} clients done` : '';
    const split = counts.total > 0 ? `${counts.succeeded} succeeded, ${counts.failed} failed` : '';
    const live = isLive(record);
    const groupClasses = [
      live ? 'deploy-row--with-reason' : '',
      open ? 'deploy-row--open' : '',
      record.deploymentId === highlightId ? 'deploy-row--new' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <Fragment key={record.deploymentId}>
        <tr className={groupClasses || undefined}>
          <td>
            <Started at={record.startedAt} />
          </td>
          <td>
            <div className="deploy-name">
              <span className="deploy-client deploy-cut" title={name}>
                {name}
              </span>
              <Badge bg="secondary">Group</Badge>
            </div>
          </td>
          <td>
            <ImageCell image={imageDisplay(record, images)} under={done} />
          </td>
          <td className="deploy-cell--label numeric" title={done}>
            {done}
          </td>
          <td className="deploy-cell--by" title={record.initiatedBy}>
            {shortUser(record.initiatedBy)}
          </td>
          <td>
            <RunDuration record={record} />
          </td>
          <td>
            <StatusPill status={record.status} />
            {split && (
              <div className="deploy-sub deploy-cut numeric" title={split}>
                {split}
              </div>
            )}
          </td>
          <td className="deploy-actions">
            <Link
              className="btn btn-sm btn-outline-secondary"
              to={`/deployments/group/${encodeURIComponent(record.deploymentId)}`}
            >
              View
            </Link>
            {detailsToggle(record, open)}
            <RunMoreMenu record={record} onStop={handlers.onStop} />
          </td>
        </tr>
        {live && (
          <tr className="deploy-row-reason">
            <td colSpan={COLUMNS}>
              <GroupRowProgress summary={record} members={groupMembers?.[record.deploymentId]} />
            </td>
          </tr>
        )}
        {open && (
          <tr className="deploy-row-details">
            <td colSpan={COLUMNS}>
              <RunDetails record={record} />
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  const renderRun = (record: DeploymentRecord) => {
    const open = Boolean(expanded[record.deploymentId]);
    const failed = isFailed(record);
    const note = statusNote(record);
    const newest = newestByClient?.get(record.clientName);
    const newerRun = newest && newest.deploymentId !== record.deploymentId ? newest : undefined;
    const blocked = liveClients?.has(record.clientName) ? BUSY_REASON : undefined;
    const failure = failures[record.deploymentId];
    // A finished plan says what it found in a line of its own. Its status cannot: see deployStatus.
    const plan = isFinishedPlan(record) ? plans?.[record.deploymentId] : undefined;
    const planText = describePlanOutcome(plan?.outcome);
    const planLine = plan?.state === 'loading' || Boolean(planText);
    // The line is there from the moment the run is live, so the row never changes height while it runs.
    const live = isLive(record);
    const snapshot = live ? (runProgress[record.deploymentId] ?? memberProgress[record.deploymentId]) : undefined;
    const classes = [
      failed || planLine || live ? 'deploy-row--with-reason' : '',
      open ? 'deploy-row--open' : '',
      record.deploymentId === highlightId ? 'deploy-row--new' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const label = record.deploymentLabel || '';
    const block = flags.deployBlock(record.clientName);

    return (
      <Fragment key={record.deploymentId}>
        <tr className={classes || undefined}>
          <td>
            <Started at={record.startedAt} />
          </td>
          <td>
            <div className="deploy-client deploy-cut" title={record.clientName}>
              {record.clientName}
            </div>
            {block && (
              <div className="deploy-sub">
                <DoNotDeployTag block={block} />
              </div>
            )}
            {isGroupMember(record) && record.groupRunId && (
              <div className="deploy-sub deploy-cut" title={`In the group run ${record.groupName || ''}`}>
                in{' '}
                <Link to={`/deployments/group/${encodeURIComponent(record.groupRunId)}`}>
                  {record.groupName || 'a group run'}
                </Link>
              </div>
            )}
          </td>
          <td>
            <ImageCell image={imageDisplay(record, images)} plan={record.mode === 'plan'} under={label} />
          </td>
          <td className="deploy-cell--label" title={label}>
            {label}
          </td>
          <td className="deploy-cell--by" title={record.initiatedBy}>
            {shortUser(record.initiatedBy)}
          </td>
          <td>
            <RunDuration record={record} />
          </td>
          <td>
            <StatusPill status={record.status} attemptNumber={record.attemptNumber} />
            {note && (
              <div className="deploy-sub deploy-cut" title={note.title}>
                {note.text}
              </div>
            )}
          </td>
          <td className="deploy-actions">
            <Link
              className="btn btn-sm btn-outline-secondary"
              to={`/deployments/${encodeURIComponent(record.deploymentId)}/logs`}
            >
              Logs
            </Link>
            {detailsToggle(record, open)}
            <RunMoreMenu record={record} {...handlers} runAgainBlocked={blocked} deployBlocked={Boolean(block)} />
          </td>
        </tr>
        {live && (
          <tr className="deploy-row-reason">
            <td colSpan={COLUMNS}>
              <RowProgress record={record} snapshot={snapshot} />
            </td>
          </tr>
        )}
        {failed && (
          <tr className="deploy-row-reason">
            <td colSpan={COLUMNS}>
              <FailureLine
                info={failure}
                newerRun={newerRun}
                onRunAgain={handlers.onRunAgain ? () => handlers.onRunAgain?.(record) : undefined}
                runAgainBlocked={blocked}
              />
            </td>
          </tr>
        )}
        {planLine && (
          <tr className="deploy-row-reason">
            <td colSpan={COLUMNS}>
              {planText ? (
                <div className="deploy-reason">
                  <span className="deploy-reason__title">Plan result</span>
                  <span className="deploy-reason__text" title={`${planText}. A plan changes nothing.`}>
                    {planText}
                  </span>
                </div>
              ) : (
                <div className="deploy-reason deploy-reason--quiet">
                  <Spinner animation="border" size="sm" className="deploy-reason__spinner" />
                  <span>Reading the log for the plan's result</span>
                </div>
              )}
            </td>
          </tr>
        )}
        {open && (
          <tr className="deploy-row-details">
            <td colSpan={COLUMNS}>
              <RunDetails record={record} failure={failure} plan={plan} />
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  return (
    <Table hover responsive className="mb-0 deploy-table deploy-table--runs" ref={table}>
      {/* Set widths for every column but Image and Label, which share what is left. */}
      <colgroup>
        <col className="deploy-col--started" />
        <col className="deploy-col--client" />
        <col />
        <col className="deploy-col--label" />
        <col className="deploy-col--by" />
        <col className="deploy-col--duration" />
        <col className="deploy-col--status" />
        <col className="deploy-col--actions" />
      </colgroup>
      <thead>
        <tr>
          <th>Started</th>
          <th>{subjectHeading}</th>
          <th>Image</th>
          <th className="deploy-cell--label">Label</th>
          <th className="deploy-cell--by">By</th>
          <th>Duration</th>
          <th>Status</th>
          <th aria-label="Actions" />
        </tr>
      </thead>
      <tbody>
        {loading && rows.length === 0 && (
          <tr>
            <td colSpan={COLUMNS} className="deploy-empty">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading deployments
            </td>
          </tr>
        )}
        {!loading && rows.length === 0 && (
          <tr>
            <td colSpan={COLUMNS} className="deploy-empty">
              {emptyText}
            </td>
          </tr>
        )}
        {rows.map((record) => (isGroupSummary(record) ? renderGroup(record) : renderRun(record)))}
      </tbody>
    </Table>
  );
}

export default HistoryTable;
